/**
 * Skin Quiz — "Hacer tu rutina"
 * ------------------------------------------------------------------
 * K-beauty routine builder for Solchis (mercado MX). Two layers:
 *
 *   SkinQuiz     — pure logic (normalize / map / score / match). No DOM,
 *                  no network. Node-testable (see scripts/skin-quiz.test.js).
 *   SkinQuizApp  — DOM controller. Reads two JSON <script> blocks rendered
 *                  by sections/skin-quiz.liquid:
 *                    • config  (labels + questions, from section settings)
 *                    • data    (products + metafields + variant id, server-side)
 *                  Renders the flow and adds to cart via /cart/add.js.
 *
 * Product data shape (one object per product, emitted by Liquid):
 *   { variantId, productId, handle, title, url, image, price, priceFormatted,
 *     available, routineStep, texture[], benefits[], skinType[], skinConcern[],
 *     reason, createdAt }
 *
 * Scoring (per spec): skin_type +30 · each skin_concern +25 · each benefit +10.
 * 수부지 (Grasa deshidratada) → skin_type "Grasa" + auto-selected concern
 * "Hidratación" (scored as a normal concern, +25). Ties → newest first.
 */

(function (global) {
  'use strict';

  /* ---------------------------------------------------------------- */
  /* Logic                                                            */
  /* ---------------------------------------------------------------- */

  class SkinQuiz {
    /** trim + lowercase for accent-preserving, case-insensitive compare. */
    static normalize(value) {
      return (value == null ? '' : String(value)).trim().toLowerCase();
    }

    /** Normalize an array of strings, dropping empties. */
    static normalizeList(arr) {
      if (!Array.isArray(arr)) return [];
      return arr.map((v) => SkinQuiz.normalize(v)).filter(Boolean);
    }

    /**
     * Map a quiz diagnosis to a skin_type metafield value.
     * Diagnoses (radio values): Seca, Grasa, Mixta, "Grasa deshidratada".
     */
    static mapSkinType(diagnosis) {
      switch (SkinQuiz.normalize(diagnosis)) {
        case 'seca': return 'Seca';
        case 'grasa': return 'Grasa';
        case 'mixta': return 'Mixta';
        case 'grasa deshidratada': return 'Grasa';
        default: return null;
      }
    }

    /**
     * Concerns auto-added by the diagnosis itself.
     * 수부지 (Grasa deshidratada) always implies the Hidratación concern
     * (skin_concern uses the 4 store categories, so dehydration → Hidratación).
     */
    static autoConcerns(diagnosis) {
      return SkinQuiz.normalize(diagnosis) === 'grasa deshidratada' ? ['Hidratación'] : [];
    }

    /**
     * Display grouping for question 4. "Tratamiento" merges the three
     * K-beauty treatment steps (Esencia / Ampoule / Sérum) into one choice.
     * `essential` groups start pre-checked in the UI.
     */
    static getGroupedRoutineSteps() {
      return [
        { key: 'Limpiador', label: 'Limpiador', steps: ['Limpiador'], essential: false },
        { key: 'Tónico', label: 'Tónico', steps: ['Tónico'], essential: true },
        { key: 'Tratamiento', label: 'Tratamiento', steps: ['Esencia', 'Ampoule', 'Sérum'], essential: true },
        { key: 'Loción / Emulsión', label: 'Loción / Emulsión', steps: ['Loción', 'Emulsión'], essential: false },
        { key: 'Crema Facial', label: 'Crema Facial', steps: ['Crema Facial'], essential: false },
        { key: 'Protector Solar', label: 'Protector Solar', steps: ['Protector Solar'], essential: true },
      ];
    }

    static groupByKey(key) {
      return SkinQuiz.getGroupedRoutineSteps().find((g) => g.key === key) || null;
    }

    /**
     * Score one product against the user's answers.
     * `selections` = { skinType, concerns[], benefits[] } (skinType already mapped).
     */
    static scoreProduct(product, selections) {
      let score = 0;
      const breakdown = {};

      const skinTypes = SkinQuiz.normalizeList(product.skinType);
      if (selections.skinType && skinTypes.includes(SkinQuiz.normalize(selections.skinType))) {
        score += 30;
        breakdown.skinType = 30;
      }

      const concerns = SkinQuiz.normalizeList(product.skinConcern);
      let concernScore = 0;
      (selections.concerns || []).forEach((c) => {
        if (concerns.includes(SkinQuiz.normalize(c))) concernScore += 25;
      });
      if (concernScore) { score += concernScore; breakdown.concerns = concernScore; }

      const benefits = SkinQuiz.normalizeList(product.benefits);
      let benefitScore = 0;
      (selections.benefits || []).forEach((b) => {
        if (benefits.includes(SkinQuiz.normalize(b))) benefitScore += 10;
      });
      if (benefitScore) { score += benefitScore; breakdown.benefits = benefitScore; }

      return { score, breakdown };
    }

    static toTime(value) {
      const t = value ? new Date(value).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    }

    /**
     * All products for a group's steps, scored and ranked best-first
     * (score desc, ties → newest). Returns [{ product, score, breakdown }].
     */
    static rankCandidates(products, group, selections) {
      const wantedSteps = group.steps.map((s) => SkinQuiz.normalize(s));
      return products
        .filter((p) => wantedSteps.includes(SkinQuiz.normalize(p.routineStep)))
        .map((p) => ({ product: p, ...SkinQuiz.scoreProduct(p, selections) }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return SkinQuiz.toTime(b.product.createdAt) - SkinQuiz.toTime(a.product.createdAt);
        });
    }

    /**
     * The "strong match" pool (within 60% of the best score). If nothing
     * matched (best 0) the whole step qualifies. Sorted best-first.
     */
    static topTier(sorted) {
      if (!sorted.length) return [];
      const best = sorted[0].score;
      if (best <= 0) return sorted.slice();
      const cutoff = best * 0.6;
      return sorted.filter((c) => c.score >= cutoff);
    }

    /** Fisher–Yates shuffle using the given rng (default Math.random). */
    static shuffle(arr, rng) {
      const rand = typeof rng === 'function' ? rng : Math.random;
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const t = a[i]; a[i] = a[j]; a[j] = t;
      }
      return a;
    }

    /**
     * Build the routine as a set of CHOICES: for each selected group, a list of
     * matching product options (the shopper picks one) plus a default pick.
     * Returns [{ group, options: Product[], defaultHandle }] in selection order.
     *
     * Strong matches are shuffled to the front (so the pre-selected option
     * varies between shoppers — "골고루"), weaker matches keep score order after
     * them, capped at `maxOptions` (default 6). The default is the first
     * in-stock option. A group with no products → options:[], defaultHandle:null.
     * rng is injectable for tests (defaults to Math.random).
     */
    static buildRoutine(products, selectedGroups, selections, rng, maxOptions) {
      const MAX = maxOptions || 6;
      return selectedGroups.map((groupKey) => {
        const group = SkinQuiz.groupByKey(groupKey);
        if (!group) return { group: groupKey, options: [], defaultHandle: null };

        const sorted = SkinQuiz.rankCandidates(products, group, selections);
        if (sorted.length === 0) return { group: groupKey, options: [], defaultHandle: null };

        const tier = SkinQuiz.topTier(sorted);
        const tierHandles = new Set(tier.map((c) => c.product.handle));
        const rest = sorted.filter((c) => !tierHandles.has(c.product.handle));
        const options = SkinQuiz.shuffle(tier, rng)
          .concat(rest)
          .slice(0, MAX)
          .map((c) => c.product);

        const firstAvailable = options.find((p) => p.available) || options[0];
        return { group: groupKey, options, defaultHandle: firstAvailable.handle };
      });
    }

    /** Build the scoring `selections` object from raw quiz state. */
    static buildSelections(state) {
      const concerns = (state.skinConcerns || []).slice();
      SkinQuiz.autoConcerns(state.skinType).forEach((c) => {
        if (!concerns.some((x) => SkinQuiz.normalize(x) === SkinQuiz.normalize(c))) concerns.push(c);
      });
      return {
        diagnosis: state.skinType,
        skinType: SkinQuiz.mapSkinType(state.skinType),
        concerns,
        benefits: state.benefits || [],
      };
    }
  }

  /* ---------------------------------------------------------------- */
  /* DOM controller (browser only)                                    */
  /* ---------------------------------------------------------------- */

  class SkinQuizApp {
    constructor(root, config, products) {
      this.root = root;
      this.config = config;
      this.products = Array.isArray(products) ? products : [];
      this.questions = config.questions || [];
      this.storageKey = 'skinQuiz-' + (root.dataset.sectionId || 'default');
      this.state = this.loadState();
    }

    defaultState() {
      // Pre-check essential routine groups per spec.
      const essentials = SkinQuiz.getGroupedRoutineSteps().filter((g) => g.essential).map((g) => g.key);
      return { step: 0, skinType: null, skinConcerns: [], benefits: [], routineSteps: essentials, routine: null, selection: {} };
    }

    loadState() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (saved) return Object.assign(this.defaultState(), saved);
      } catch (_) { /* ignore */ }
      return this.defaultState();
    }

    saveState() {
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch (_) { /* ignore */ }
    }

    $(sel) { return this.root.querySelector(sel); }
    label(key) { return (this.config.labels && this.config.labels[key]) || ''; }

    start() {
      this.formsEl = this.$('[data-quiz-forms]');
      this.progressEl = this.$('[data-quiz-progress]');
      this.resultsEl = this.$('[data-quiz-results]');
      this.root.addEventListener('click', (e) => {
        if (e.target.closest('[data-quiz-restart]')) { e.preventDefault(); this.restart(); }
      });
      // A Liquid loop renders max 50 products/page; pull the rest in the
      // background via the Section Rendering API so the pool is complete
      // before the user reaches the results.
      this.ready = this.hydrateAllPages();
      this.render();
    }

    async hydrateAllPages() {
      const ds = this.root.dataset;
      let hasNext = ds.hasNext === 'true';
      if (!hasNext) return;
      const path = ds.path || window.location.pathname;
      const renderId = ds.renderId;
      let page = parseInt(ds.page || '1', 10) || 1;
      let guard = 0;
      while (hasNext && guard < 25) {
        guard += 1;
        page += 1;
        try {
          const url = `${path}?section_id=${encodeURIComponent(renderId)}&page=${page}`;
          const res = await fetch(url, { headers: { Accept: 'text/html' } });
          if (!res.ok) break;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const dataEl = doc.querySelector('[data-quiz-data]');
          const rootEl = doc.querySelector('[data-skin-quiz]');
          if (dataEl) {
            const more = JSON.parse(dataEl.textContent);
            if (Array.isArray(more) && more.length) this.products = this.products.concat(more);
          }
          hasNext = rootEl ? rootEl.dataset.hasNext === 'true' : false;
        } catch (e) {
          console.warn('[skin-quiz] page stitch stopped; using products loaded so far.', e);
          break;
        }
      }
    }

    render() {
      this.renderProgress();
      this.renderQuestion();
    }

    renderProgress() {
      this.progressEl.innerHTML = this.questions.map((_, i) => {
        const cls = i < this.state.step ? 'completed' : i === this.state.step ? 'active' : '';
        return `<span class="skin-quiz__progress-dot ${cls}"></span>`;
      }).join('');
    }

    renderQuestion() {
      const i = this.state.step;
      const q = this.questions[i];
      const isLast = i === this.questions.length - 1;
      let body = '';

      if (q.type === 'radio') {
        body = '<div class="skin-quiz__options">' + q.options.map((opt) => {
          const sel = this.state.skinType === opt.value;
          return `<label class="skin-quiz__option ${sel ? 'selected' : ''}">
            <input type="radio" class="skin-quiz__option-input" name="${q.id}" value="${escAttr(opt.value)}" ${sel ? 'checked' : ''}>
            <span class="skin-quiz__option-label">
              <span class="skin-quiz__option-title">${esc(opt.title)}</span>
              ${opt.description ? `<span class="skin-quiz__option-description">${esc(opt.description)}</span>` : ''}
            </span>
          </label>`;
        }).join('') + '</div>';
      } else if (q.type === 'checkbox') {
        const chosen = this.state[q.id] || [];
        body = '<div class="skin-quiz__checkbox-group">' + q.options.map((opt) => {
          const sel = chosen.includes(opt.value);
          return `<label class="skin-quiz__checkbox-item ${sel ? 'selected' : ''}">
            <input type="checkbox" class="skin-quiz__checkbox-input" name="${q.id}" value="${escAttr(opt.value)}" ${sel ? 'checked' : ''}>
            <span class="skin-quiz__checkbox-label">${esc(opt.title)}</span>
          </label>`;
        }).join('') + '</div>';
      } else if (q.type === 'steps') {
        const chosen = this.state.routineSteps || [];
        body = (q.note ? `<p class="skin-quiz__description">${esc(q.note)}</p>` : '')
          + '<div class="skin-quiz__checkbox-group">'
          + SkinQuiz.getGroupedRoutineSteps().map((g) => {
            const sel = chosen.includes(g.key);
            const badge = g.essential ? `<span class="skin-quiz__badge essential">${esc(this.label('essential'))}</span>` : '';
            return `<label class="skin-quiz__checkbox-item ${sel ? 'selected' : ''}">
              <input type="checkbox" class="skin-quiz__checkbox-input" name="routineSteps" value="${escAttr(g.key)}" ${sel ? 'checked' : ''}>
              <span class="skin-quiz__checkbox-label">${badge}${esc(g.label)}</span>
            </label>`;
          }).join('') + '</div>';
      }

      this.formsEl.innerHTML = `
        <form class="skin-quiz__form active" novalidate>
          <div class="skin-quiz__question">
            <h2 class="skin-quiz__question-title">${esc(q.title)}</h2>
            ${body}
          </div>
          <div class="skin-quiz__buttons">
            ${i > 0 ? `<button type="button" class="skin-quiz__button" data-quiz-prev>${esc(this.label('prev'))}</button>` : ''}
            <button type="button" class="skin-quiz__button primary" data-quiz-next>${esc(isLast ? this.label('submit') : this.label('next'))}</button>
          </div>
        </form>`;

      this.formsEl.style.display = 'block';
      this.resultsEl.classList.remove('active');
      this.bindQuestion(q);
    }

    bindQuestion(q) {
      const form = this.formsEl.querySelector('form');

      form.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener('change', () => {
          this.state.skinType = input.value;
          this.saveState();
          this.syncSelected(form);
        });
      });

      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.addEventListener('change', () => {
          const key = q.type === 'steps' ? 'routineSteps' : q.id;
          const list = this.state[key] || (this.state[key] = []);
          const idx = list.indexOf(input.value);
          if (idx > -1) list.splice(idx, 1); else list.push(input.value);
          this.saveState();
          this.syncSelected(form);
        });
      });

      form.querySelector('[data-quiz-prev]')?.addEventListener('click', () => this.go(-1));
      form.querySelector('[data-quiz-next]')?.addEventListener('click', () => {
        if (this.validate(q)) {
          if (this.state.step === this.questions.length - 1) this.submit();
          else this.go(1);
        }
      });
    }

    syncSelected(form) {
      form.querySelectorAll('input').forEach((input) => {
        const on = input.type === 'radio'
          ? this.state.skinType === input.value
          : (this.state[input.name] || []).includes(input.value);
        input.closest('label').classList.toggle('selected', on);
      });
    }

    validate(q) {
      if (q.type === 'radio' && !this.state.skinType) { alert(this.label('validation')); return false; }
      if (q.type === 'checkbox' && (this.state[q.id] || []).length === 0) { alert(this.label('validation')); return false; }
      if (q.type === 'steps') {
        const n = (this.state.routineSteps || []).length;
        if (n < 2) { alert(this.label('minSteps')); return false; }
        if (n > 5) { alert(this.label('maxSteps')); return false; }
      }
      return true;
    }

    go(delta) {
      this.state.step = Math.max(0, Math.min(this.questions.length - 1, this.state.step + delta));
      this.saveState();
      this.render();
      this.scrollTop();
    }

    async submit() {
      const nextBtn = this.formsEl.querySelector('[data-quiz-next]');
      if (nextBtn) nextBtn.disabled = true;
      try { if (this.ready) await this.ready; } catch (_) { /* use what we have */ }
      const selections = SkinQuiz.buildSelections(this.state);
      // Keep the user's chosen group order.
      const orderedGroups = SkinQuiz.getGroupedRoutineSteps()
        .map((g) => g.key)
        .filter((k) => this.state.routineSteps.includes(k));
      this.state.routine = SkinQuiz.buildRoutine(this.products, orderedGroups, selections);
      // Seed each step's selection with its default (first in-stock) option.
      this.state.selection = {};
      this.state.routine.forEach((r) => { if (r.defaultHandle) this.state.selection[r.group] = r.defaultHandle; });
      this.saveState();
      this.renderResults();
    }

    renderResults() {
      this.formsEl.style.display = 'none';
      this.resultsEl.classList.add('active');
      const grid = this.resultsEl.querySelector('[data-quiz-results-grid]');
      grid.classList.add('skin-quiz__grid--routine');
      const routine = this.state.routine || [];

      const steps = routine.map((r) => this.stepHTML(r)).join('');
      const addLabel = this.label('addRoutine') || 'Añadir mi rutina al carrito';
      grid.innerHTML = `
        <div class="skin-quiz__routine">${steps}</div>
        <div class="skin-quiz__routine-foot" data-routine-actions>
          <button type="button" class="skin-quiz__button primary skin-quiz__routine-add" data-quiz-add-routine>
            ${esc(addLabel)} (<span data-routine-count>0</span>)
          </button>
        </div>`;

      grid.querySelectorAll('input[type="radio"][data-group]').forEach((input) => {
        input.addEventListener('change', () => {
          this.state.selection[input.dataset.group] = input.value;
          this.saveState();
          this.syncRoutineSelected(grid);
          this.updateRoutineCount(grid);
        });
      });
      this.syncRoutineSelected(grid);
      this.updateRoutineCount(grid);

      const addBtn = grid.querySelector('[data-quiz-add-routine]');
      if (addBtn) addBtn.addEventListener('click', () => this.addRoutineToCart(grid));
      this.scrollTop();
    }

    /** One routine step: its options as selectable rows (or a "próximamente" note). */
    stepHTML(r) {
      const badge = `<span class="skin-quiz__badge">${esc(r.group)}</span>`;
      if (!r.options || r.options.length === 0) {
        return `<div class="skin-quiz__step">
          <div class="skin-quiz__step-head">${badge}</div>
          <p class="skin-quiz__step-empty">${esc(this.label('comingSoonLabel'))}</p>
        </div>`;
      }
      const selected = this.state.selection[r.group];
      const name = 'routine-' + r.group.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const options = r.options.map((p) => this.optionHTML(p, r.group, name, p.handle === selected)).join('');
      return `<div class="skin-quiz__step">
        <div class="skin-quiz__step-head">${badge}</div>
        <div class="skin-quiz__step-options">${options}</div>
      </div>`;
    }

    /** A single selectable product row (radio + image + title + price + description). */
    optionHTML(p, group, name, checked) {
      const img = p.image
        ? `<img src="${escAttr(p.image)}" alt="${escAttr(p.title)}" class="skin-quiz__opt-image" loading="lazy" width="72" height="72">`
        : `<span class="skin-quiz__opt-image skin-quiz__opt-image--ph"></span>`;
      const desc = p.reason || p.description;
      return `<label class="skin-quiz__opt${checked ? ' selected' : ''}${p.available ? '' : ' disabled'}">
        <input type="radio" class="skin-quiz__opt-input" name="${escAttr(name)}" value="${escAttr(p.handle)}"
          data-group="${escAttr(group)}" data-variant="${escAttr(p.variantId)}"
          ${checked ? 'checked' : ''} ${p.available ? '' : 'disabled'}>
        <span class="skin-quiz__opt-media">${img}</span>
        <span class="skin-quiz__opt-body">
          <span class="skin-quiz__opt-row">
            <a href="${escAttr(p.url)}" class="skin-quiz__opt-title" target="_blank" rel="noopener">${esc(p.title)}</a>
            <span class="skin-quiz__opt-price">${esc(p.priceFormatted)}</span>
          </span>
          ${desc ? `<span class="skin-quiz__opt-desc">${esc(desc)}</span>` : ''}
          ${p.available ? '' : `<span class="skin-quiz__opt-soldout">${esc(this.label('soldOut'))}</span>`}
        </span>
      </label>`;
    }

    syncRoutineSelected(grid) {
      grid.querySelectorAll('.skin-quiz__opt').forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        label.classList.toggle('selected', !!input && input.checked);
      });
    }

    /** The in-stock product currently chosen in each step. */
    selectedItems() {
      const items = [];
      (this.state.routine || []).forEach((r) => {
        const opt = (r.options || []).find((o) => o.handle === this.state.selection[r.group]);
        if (opt && opt.available) items.push(opt);
      });
      return items;
    }

    updateRoutineCount(grid) {
      const n = this.selectedItems().length;
      const countEl = grid.querySelector('[data-routine-count]');
      if (countEl) countEl.textContent = n;
      const btn = grid.querySelector('[data-quiz-add-routine]');
      if (btn) btn.disabled = n === 0;
    }

    async addRoutineToCart(grid) {
      const items = this.selectedItems();
      if (!items.length) return;
      const btn = grid.querySelector('[data-quiz-add-routine]');
      const foot = grid.querySelector('[data-routine-actions]');
      const original = btn ? btn.innerHTML : '';
      if (btn) { btn.disabled = true; btn.textContent = this.label('adding') || 'Añadiendo…'; }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items.map((p) => ({ id: Number(p.variantId), quantity: 1 })) }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'add_failed');
        }
        if (foot) {
          foot.innerHTML = `<span class="skin-quiz__added">✓ ${esc(this.label('added'))}</span>
            <a class="skin-quiz__cart-link" href="/cart">${esc(this.label('viewCart'))}</a>`;
        }
        document.dispatchEvent(new CustomEvent('skin-quiz:added'));
        this.refreshCart();
      } catch (e) {
        if (btn) { btn.disabled = false; btn.innerHTML = original; }
        alert(this.label('error'));
      }
    }

    // Best-effort: refresh the theme's cart bubble/drawer without hard coupling.
    refreshCart() {
      fetch('/cart.js').then((r) => r.json()).then((cart) => {
        document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach((el) => {
          const n = el.querySelector('span[aria-hidden="true"]') || el;
          if (n) n.textContent = cart.item_count;
        });
      }).catch(() => { /* non-fatal: item is already in the cart */ });
    }

    restart() {
      this.state = this.defaultState();
      this.saveState();
      this.render();
      this.scrollTop();
    }

    scrollTop() {
      const y = this.root.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  /* ---------------------------------------------------------------- */
  /* HTML escaping helpers                                            */
  /* ---------------------------------------------------------------- */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escAttr(s) { return esc(s); }

  /* ---------------------------------------------------------------- */
  /* Bootstrap                                                        */
  /* ---------------------------------------------------------------- */

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { console.error('[skin-quiz] bad JSON', e); return null; }
  }

  function initSkinQuiz(root) {
    const config = readJSON(root.querySelector('[data-quiz-config]'));
    const products = readJSON(root.querySelector('[data-quiz-data]')) || [];
    if (!config) { console.error('[skin-quiz] missing config'); return; }
    new SkinQuizApp(root, config, products).start();
  }

  if (typeof document !== 'undefined') {
    const boot = () => document.querySelectorAll('[data-skin-quiz]').forEach(initSkinQuiz);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    // Re-init inside the Shopify theme editor when the section is reloaded.
    document.addEventListener('shopify:section:load', (e) => {
      e.target.querySelectorAll('[data-skin-quiz]').forEach(initSkinQuiz);
    });
  }

  // Exports: logic for tests/SSR; controller for the browser.
  global.SkinQuiz = SkinQuiz;
  global.SkinQuizApp = SkinQuizApp;
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { SkinQuiz, SkinQuizApp };
  }
})(typeof window !== 'undefined' ? window : globalThis);
